import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronLeft, FileText, User, Calendar, AlertCircle, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { usePracticeStore } from '@/store/practiceStore';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';

import { toast } from 'sonner';
import { parseItalianDateInput, formatItalianDateInput, formatItalianDate } from '@/utils/date';
import { DatePickerItalian } from '@/components/ui/date-picker-italian';
import { apiClient } from '@/utils/api';
import { usePracticeBasePath } from '@/hooks/usePracticeBasePath';
import { useClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';
import { savePracticeHandle } from '@/hooks/useFileSystem';
import { sanitizeFolderName } from '@/utils/sanitizeFolderName';
import { joinUncDocumentsPath, uncPathLastSegment } from '@/utils/joinUncPath';
import { openDB } from 'idb';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import {
  findMatchingPracticeFoldersBrowser,
  findMatchingPracticeFoldersElectron,
  getNextPracticeNumberElectron,
  invalidateDirectoryCache,
} from '@/utils/practiceFolderScan';
import { normalizeDocumentPath, DEFAULT_NAS_PICKER_ROOT } from '@/utils/normalizeDocumentPath';

const FOLDER_TEMPLATE_DEFAULT = '{practiceNumber} - {clientName} - {interventionLocation} - {practiceTypeName}';

type NasChoiceResult =
  | { action: 'associate'; folderName: string; createSubfolders: boolean }
  | { action: 'create' };

type Step = 'type' | 'info' | 'dates' | 'review';

export default function PracticeFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [parentId, setParentId] = useState(searchParams.get('parentId') || '');
  const [parentPractice, setParentPractice] = useState<{
    id: string; title: string; practiceNumber?: string | null;
    clientId: string; client?: { firstName: string; lastName: string };
    interventionLocation?: string;
  } | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [formData, setFormData] = useState({
    typeId: '',
    practiceNumber: '',
    title: '',
    description: '',
    clientId: searchParams.get('clientId') || '',
    assignedToId: '',
    interventionLocation: '',
    priority: 'MEDIUM',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    amount: '',
  });
  const { baseHandle, displayName, isSupported, hasSavedPath, selectBasePath, requestPermissionAndReload } = usePracticeBasePath();
  const { documentsNasRoot } = useClientDocumentsConfig();
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    existingPractice: { id: string; title: string; practiceNumber?: string; client?: { firstName: string; lastName: string }; type?: { name: string; code: string } } | null;
  }>({ open: false, existingPractice: null });

  const inElectron = isElectronEnvironment() || !!(typeof window !== 'undefined' && window.isElectron);
  const [showNasMatchDialog, setShowNasMatchDialog] = useState(false);
  const [nasMatchCandidates, setNasMatchCandidates] = useState<string[]>([]);
  const [selectedNasFolder, setSelectedNasFolder] = useState<string>('');
  const [nasAssociateCreateSubfolders, setNasAssociateCreateSubfolders] = useState(true);
  const nasChoiceResolverRef = useRef<((r: NasChoiceResult) => void) | null>(null);
  const [suggestedNextNumber, setSuggestedNextNumber] = useState<number | null>(null);
  const [loadingNextNumber, setLoadingNextNumber] = useState(false);

  const {
    createPractice,
    updatePractice,
    loading,
  } = usePracticeStore();

  const {
    practiceTypes,
    fetchPracticeTypes,
    loading: typesLoading,
  } = usePracticeTypeStore();

  const [clients, setClients] = useState<Array<{ id: string; firstName: string; lastName: string; email?: string; companyName?: string | null }>>([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    fetchPracticeTypes(false);
    apiClient.get('/clients/combobox')
      .then((res) => setClients(res.data?.data || []))
      .catch(() => {})
      .finally(() => setClientsLoading(false));
    apiClient.get('/users/assignable')
      .then((response) => {
        setUsers(response.data?.data || []);
      })
      .catch(() => {});
  }, [fetchPracticeTypes]);

  useEffect(() => {
    if (!parentId) return;
    apiClient.get(`/practices/${parentId}`)
      .then((res) => {
        const p = res.data?.data?.practice;
        if (p) {
          setParentPractice({
            id: p.id,
            title: p.title,
            practiceNumber: p.practiceNumber,
            clientId: p.clientId,
            client: p.client ? { firstName: p.client.firstName, lastName: p.client.lastName } : undefined,
            interventionLocation: p.interventionLocation,
          });
          setFormData((prev) => ({
            ...prev,
            clientId: p.clientId || prev.clientId,
            practiceNumber: p.practiceNumber || prev.practiceNumber,
            interventionLocation: p.interventionLocation || prev.interventionLocation,
          }));
        }
      })
      .catch(() => {});
  }, [parentId]);

  useEffect(() => {
    if (!inElectron || !window.electronFS) return;
    setLoadingNextNumber(true);
    const timer = setTimeout(() => {
      try {
        const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
        if (!window.electronFS!.directoryExists(volumePath)) { setLoadingNextNumber(false); return; }
        const next = getNextPracticeNumberElectron(volumePath);
        setSuggestedNextNumber(next);
      } catch {
        /* NAS non raggiungibile */
      }
      setLoadingNextNumber(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [inElectron, documentsNasRoot]);

  // Genera automaticamente il titolo quando cambiano tipo pratica o cliente
  useEffect(() => {
    if (formData.typeId && formData.clientId) {
      const type = practiceTypes.find(t => t.id === formData.typeId);
      const client = clients.find(c => c.id === formData.clientId);
      if (type && client) {
        const autoTitle = `${type.code || type.name} - ${client.firstName} ${client.lastName}`;
        // Aggiorna solo se l'utente non ha modificato manualmente il titolo
        // (confronta con l'ultimo titolo auto-generato)
        setFormData(prev => {
          const lastAutoTitle = prev.title === `${type.code || type.name} - ${client.firstName} ${client.lastName}` ||
                                prev.title === `${type.code || type.name} - ${client.firstName}${client.lastName}` ||
                                prev.title === `${type.code || type.name} -${client.firstName} ${client.lastName}` ||
                                prev.title.startsWith(`${type.code || type.name} -`);
          
          // Se il titolo attuale è vuoto o corrisponde a un auto-titolo precedente, aggiorna
          if (!prev.title || lastAutoTitle) {
            return { ...prev, title: autoTitle };
          }
          return prev;
        });
      }
    }
  }, [formData.typeId, formData.clientId, practiceTypes, clients]);

  const steps: { id: Step; title: string; description: string }[] = [
    { id: 'type', title: 'Tipo Pratica', description: 'Seleziona il tipo di pratica' },
    { id: 'info', title: 'Informazioni', description: 'Titolo, cliente e descrizione' },
    { id: 'dates', title: 'Scadenze', description: 'Date e priorità' },
    { id: 'review', title: 'Riepilogo', description: 'Verifica i dati inseriti' },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const canGoNext = () => {
    switch (currentStep) {
      case 'type':
        return !!formData.typeId;
      case 'info':
        return !!formData.title && !!formData.clientId && (!!parentId || !!formData.practiceNumber.trim());
      case 'dates':
        return true; // Tutti i campi sono opzionali
      case 'review':
        return false;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canGoNext()) {
      if (currentStepIndex < steps.length - 1) {
        setCurrentStep(steps[currentStepIndex + 1].id);
      }
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  /** Crea cartelle e salva l'handle. */
  const createFolderStructure = async (parentHandle: FileSystemDirectoryHandle, newPracticeId: string) => {
    const expectedRoot = uncPathLastSegment(documentsNasRoot);
    if (expectedRoot && parentHandle.name.toLowerCase() !== expectedRoot.toLowerCase()) {
      toast.error(
        `Seleziona la cartella "${expectedRoot}" (ultimo segmento del percorso NAS in Impostazioni). Attuale: "${parentHandle.name}".`
      );
      throw new Error('NAS_ROOT_MISMATCH');
    }

    const folderTemplate = selectedType?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;
    const rawFolderName = folderTemplate
      .replace('{practiceNumber}', formData.practiceNumber || 'SENZA_NUMERO')
      .replace('{clientName}', selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : '')
      .replace('{interventionLocation}', formData.interventionLocation || 'N/D')
      .replace('{practiceTypeName}', selectedType?.name || '');
    const folderName = sanitizeFolderName(rawFolderName);

    const practiceFolder = await (parentHandle as any).getDirectoryHandle(folderName, { create: true });

    const subfolders = selectedType?.folderSubfolders || [];
    for (const sub of subfolders) {
      if (sub) {
        const safeName = sanitizeFolderName(sub);
        await practiceFolder.getDirectoryHandle(safeName, { create: true });
      }
    }

    const uncDocumentPath = joinUncDocumentsPath(documentsNasRoot, folderName);
    await savePracticeHandle(newPracticeId, practiceFolder, uncDocumentPath);
    await apiClient.patch(`/practices/${newPracticeId}/document-path`, { documentPath: uncDocumentPath });
    toast.success('Struttura cartelle creata');
  };

  /** Electron: crea cartella + sottocartelle sul path UNC del Volume */
  const createFolderStructureElectron = async (newPracticeId: string) => {
    const efs = window.electronFS;
    if (!efs) throw new Error('NO_ELECTRON_FS');
    const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
    const folderTemplate = selectedType?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;
    const rawFolderName = folderTemplate
      .replace('{practiceNumber}', formData.practiceNumber || 'SENZA_NUMERO')
      .replace('{clientName}', selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : '')
      .replace('{interventionLocation}', formData.interventionLocation || 'N/D')
      .replace('{practiceTypeName}', selectedType?.name || '');
    const folderName = sanitizeFolderName(rawFolderName);
    const fullPath = efs.joinPath(volumePath, folderName);
    efs.createDirectory(fullPath);
    const subfolders = selectedType?.folderSubfolders || [];
    for (const sub of subfolders) {
      if (sub) {
        const safeName = sanitizeFolderName(sub);
        efs.createDirectory(efs.joinPath(fullPath, safeName));
      }
    }
    const uncDocumentPath = joinUncDocumentsPath(documentsNasRoot, folderName);
    await apiClient.patch(`/practices/${newPracticeId}/document-path`, { documentPath: uncDocumentPath });
    invalidateDirectoryCache();
    toast.success('Struttura cartelle creata');
  };

  const associateExistingFolderBrowser = async (
    parentHandle: FileSystemDirectoryHandle,
    folderName: string,
    newPracticeId: string,
    createSubfolders: boolean
  ) => {
    const practiceFolder = await (parentHandle as any).getDirectoryHandle(folderName, { create: false });
    const subfolders = selectedType?.folderSubfolders || [];
    if (createSubfolders) {
      for (const sub of subfolders) {
        if (sub) {
          const safeName = sanitizeFolderName(sub);
          try {
            await practiceFolder.getDirectoryHandle(safeName, { create: true });
          } catch {
            /* esiste già */
          }
        }
      }
    }
    const uncDocumentPath = joinUncDocumentsPath(documentsNasRoot, folderName);
    await savePracticeHandle(newPracticeId, practiceFolder, uncDocumentPath);
    await apiClient.patch(`/practices/${newPracticeId}/document-path`, { documentPath: uncDocumentPath });
    toast.success('Cartella esistente associata alla pratica');
  };

  const associateExistingFolderElectron = async (
    folderName: string,
    newPracticeId: string,
    createSubfolders: boolean
  ) => {
    const efs = window.electronFS;
    if (!efs) throw new Error('NO_ELECTRON_FS');
    const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
    const fullPath = efs.joinPath(volumePath, folderName);
    if (!efs.directoryExists(fullPath)) {
      toast.error('Cartella non trovata sul NAS');
      return;
    }
    const subfolders = selectedType?.folderSubfolders || [];
    if (createSubfolders) {
      for (const sub of subfolders) {
        if (sub) {
          const safeName = sanitizeFolderName(sub);
          try {
            efs.createDirectory(efs.joinPath(fullPath, safeName));
          } catch {
            /* */
          }
        }
      }
    }
    const uncDocumentPath = joinUncDocumentsPath(documentsNasRoot, folderName);
    await apiClient.patch(`/practices/${newPracticeId}/document-path`, { documentPath: uncDocumentPath });
    toast.success('Cartella esistente associata alla pratica');
  };

  const waitForNasFolderChoice = (candidates: string[]): Promise<NasChoiceResult> => {
    return new Promise((resolve) => {
      nasChoiceResolverRef.current = resolve;
      setNasMatchCandidates(candidates);
      setSelectedNasFolder(candidates[0] || '');
      setNasAssociateCreateSubfolders(true);
      setShowNasMatchDialog(true);
    });
  };

  const resolveNasChoice = (result: NasChoiceResult) => {
    setShowNasMatchDialog(false);
    const fn = nasChoiceResolverRef.current;
    nasChoiceResolverRef.current = null;
    fn?.(result);
  };

  const processFoldersAfterCreate = async (
    newPracticeId: string,
    parentHandle: FileSystemDirectoryHandle | null
  ) => {
    const folderTemplate = selectedType?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;
    if (!folderTemplate) return;

    const pnum = formData.practiceNumber?.trim();
    if (!pnum || pnum.length < 2) {
      if (parentHandle) await createFolderStructure(parentHandle, newPracticeId);
      else if (inElectron && window.electronFS) await createFolderStructureElectron(newPracticeId);
      return;
    }

    if (inElectron && window.electronFS) {
      const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
      console.log('[PracticeForm] Electron NAS check:', { volumePath, pnum, documentsNasRoot });
      if (!window.electronFS.directoryExists(volumePath)) {
        console.warn('[PracticeForm] NAS path not accessible:', volumePath);
        toast.error(
          'Percorso NAS non raggiungibile da questa sessione. Verifica Impostazioni documenti.'
        );
        return;
      }
      const candidates = findMatchingPracticeFoldersElectron(volumePath, pnum);
      console.log('[PracticeForm] NAS folder candidates:', candidates);
      if (candidates.length > 0) {
        const choice = await waitForNasFolderChoice(candidates);
        if (choice.action === 'associate') {
          await associateExistingFolderElectron(choice.folderName, newPracticeId, choice.createSubfolders);
        } else {
          await createFolderStructureElectron(newPracticeId);
        }
        return;
      }
      await createFolderStructureElectron(newPracticeId);
      return;
    }

    if (parentHandle) {
      const candidates = await findMatchingPracticeFoldersBrowser(parentHandle, pnum);
      if (candidates.length > 0) {
        const choice = await waitForNasFolderChoice(candidates);
        if (choice.action === 'associate') {
          await associateExistingFolderBrowser(
            parentHandle,
            choice.folderName,
            newPracticeId,
            choice.createSubfolders
          );
        } else {
          await createFolderStructure(parentHandle, newPracticeId);
        }
        return;
      }
      await createFolderStructure(parentHandle, newPracticeId);
    }
  };

  /**
   * Verifica e rinnova il permesso sull'handle prima di procedere.
   * Tenta requestPermission come fallback (funziona se siamo in catena da user gesture).
   */
  const ensurePermission = async (handle: FileSystemDirectoryHandle): Promise<boolean> => {
    try {
      const h = handle as any;
      const perm = await h.queryPermission?.({ mode: 'readwrite' });
      if (perm === 'granted') return true;
      const req = await h.requestPermission?.({ mode: 'readwrite' });
      return req === 'granted';
    } catch {
      return false;
    }
  };

  const doSubmitAsSubPractice = async (overrideParentId: string) => {
    try {
      let dueDateISO: string | undefined = undefined;
      if (formData.dueDate && formData.dueDate.trim() !== '') {
        const parsedDate = parseItalianDateInput(formData.dueDate);
        if (parsedDate) {
          dueDateISO = parsedDate.toISOString();
        } else {
          toast.error('Data di scadenza non valida. Usa il formato GG MM AAAA');
          return;
        }
      }

      const practiceData: any = {
        typeId: formData.typeId,
        practiceNumber: formData.practiceNumber || undefined,
        title: formData.title,
        description: formData.description || undefined,
        clientId: formData.clientId,
        assignedToId: formData.assignedToId || undefined,
        interventionLocation: formData.interventionLocation || undefined,
        priority: formData.priority || 'MEDIUM',
        dueDate: dueDateISO,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
        parentId: overrideParentId,
      };

      const newPractice = await createPractice(practiceData);
      toast.success('Sotto-pratica creata con successo');

      if (inElectron && window.electronFS && selectedType) {
        try {
          const parentDocPath = await apiClient.get(`/practices/${overrideParentId}`).then(r => r.data?.data?.practice?.documentPath);
          if (parentDocPath) {
            const efs = window.electronFS;
            const normalizedParent = normalizeDocumentPath(parentDocPath) || parentDocPath;
            const subFolderName = sanitizeFolderName(selectedType.code || selectedType.name);
            const subPath = efs.joinPath(normalizedParent, subFolderName);
            efs.createDirectory(subPath);
            const subfolders = selectedType?.folderSubfolders || [];
            for (const sub of subfolders) {
              if (sub) efs.createDirectory(efs.joinPath(subPath, sanitizeFolderName(sub)));
            }
            const uncSubPath = parentDocPath.endsWith('\\') || parentDocPath.endsWith('/')
              ? `${parentDocPath}${subFolderName}`
              : `${parentDocPath}\\${subFolderName}`;
            await apiClient.patch(`/practices/${newPractice.id}/document-path`, { documentPath: uncSubPath });
            toast.success('Sotto-cartella creata nella pratica padre');
          }
        } catch (err: any) {
          console.error('Sub-practice folder creation error:', err);
        }
      }

      navigate(`/practices/${newPractice.id}`);
    } catch (error: any) {
      console.error('Error creating sub-practice:', error);
      toast.error(error.response?.data?.message || 'Errore nella creazione della sotto-pratica');
    }
  };

  const doSubmit = async (parentHandle: FileSystemDirectoryHandle | null) => {
    try {
      // Se abbiamo un parentHandle, verifichiamo/rinnoviamo il permesso PRIMA del network call
      if (parentHandle) {
        const hasAccess = await ensurePermission(parentHandle);
        if (!hasAccess) {
          toast.error('Permesso di accesso alla cartella negato. Riautorizza e riprova.');
          return;
        }
        // Test di scrittura: prova a leggere la directory per confermare accesso reale
        try {
          const testIter = (parentHandle as any).values();
          await testIter.next();
        } catch (testErr: any) {
          console.error('Access test failed:', testErr);
          toast.error(`Impossibile accedere alla cartella: ${testErr.message}`);
          return;
        }
      }

      let dueDateISO: string | undefined = undefined;
      if (formData.dueDate && formData.dueDate.trim() !== '') {
        const parsedDate = parseItalianDateInput(formData.dueDate);
        if (parsedDate) {
          dueDateISO = parsedDate.toISOString();
        } else {
          toast.error('Data di scadenza non valida. Usa il formato GG MM AAAA');
          return;
        }
      }

      const practiceData: any = {
        typeId: formData.typeId,
        practiceNumber: formData.practiceNumber || undefined,
        title: formData.title,
        description: formData.description || undefined,
        clientId: formData.clientId,
        assignedToId: formData.assignedToId || undefined,
        interventionLocation: formData.interventionLocation || undefined,
        priority: formData.priority || 'MEDIUM',
        dueDate: dueDateISO,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
        ...(parentId ? { parentId } : {}),
      };

      const newPractice = await createPractice(practiceData);
      toast.success(parentId ? 'Sotto-pratica creata con successo' : 'Pratica creata con successo');

      if (parentId && parentPractice && inElectron && window.electronFS && selectedType) {
        try {
          const parentDocPath = await apiClient.get(`/practices/${parentId}`).then(r => r.data?.data?.practice?.documentPath);
          if (parentDocPath) {
            const efs = window.electronFS;
            const normalizedParent = normalizeDocumentPath(parentDocPath) || parentDocPath;
            const subFolderName = sanitizeFolderName(selectedType.code || selectedType.name);
            const subPath = efs.joinPath(normalizedParent, subFolderName);
            efs.createDirectory(subPath);
            const subfolders = selectedType?.folderSubfolders || [];
            for (const sub of subfolders) {
              if (sub) efs.createDirectory(efs.joinPath(subPath, sanitizeFolderName(sub)));
            }
            const uncSubPath = parentDocPath.endsWith('\\') || parentDocPath.endsWith('/')
              ? `${parentDocPath}${subFolderName}`
              : `${parentDocPath}\\${subFolderName}`;
            await apiClient.patch(`/practices/${newPractice.id}/document-path`, { documentPath: uncSubPath });
            toast.success('Sotto-cartella creata nella pratica padre');
          }
        } catch (err: any) {
          console.error('Sub-practice folder creation error:', err);
        }
      } else if (!parentId) {
        const folderTpl = selectedType?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;
        const needsFolderFlow = !!folderTpl && (inElectron || isSupported);

        if (needsFolderFlow) {
          try {
            await processFoldersAfterCreate(newPractice.id, parentHandle);
          } catch (folderError: any) {
            if (folderError.name !== 'AbortError') {
              console.error('Error creating folder structure:', folderError);
              toast.error(
                `Errore cartelle: ${folderError.message || 'errore sconosciuto'}. Potrai configurarle dalla pratica.`
              );
            }
          }
        }
      }

      navigate(`/practices/${newPractice.id}`);
    } catch (error: any) {
      console.error('Error creating practice:', error);
      if (error.response?.data?.code === 'PRACTICE_NUMBER_EXISTS' && error.response.data.existingPractice) {
        setDuplicateDialog({ open: true, existingPractice: error.response.data.existingPractice });
        return;
      }
      toast.error(error.response?.data?.message || 'Errore nella creazione della pratica');
    }
  };

  const handleSubmit = async () => {
    if (parentId) {
      await doSubmit(null);
      return;
    }

    const folderTemplate = selectedType?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;
    const needsFolder = !!folderTemplate && (inElectron || isSupported);

    if (!needsFolder) {
      await doSubmit(null);
      return;
    }

    if (inElectron) {
      await doSubmit(null);
      return;
    }

    if (baseHandle) {
      const hasAccess = await ensurePermission(baseHandle);
      if (hasAccess) {
        await doSubmit(baseHandle);
        return;
      }
    }

    setPendingSubmit(true);
    setShowFolderDialog(true);
  };

  const handleFolderDialogSelect = async () => {
    if (inElectron && window.electronDialog) {
      try {
        const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
        const picked = await window.electronDialog.pickDirectory(volumePath);
        if (picked) {
          setShowFolderDialog(false);
          setPendingSubmit(false);
          await doSubmit(null);
        }
      } catch (err: any) {
        toast.error(`Errore nella selezione della cartella: ${err?.message || 'sconosciuto'}`);
      }
      return;
    }
    try {
      const result = await selectBasePath();
      if (result) {
        setShowFolderDialog(false);
        await doSubmit(result.handle);
        setPendingSubmit(false);
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      if (err?.name === 'SecurityError' || err?.message?.includes('sistema')) {
        toast.error(
          'Chrome ha bloccato questa cartella. Prova a selezionare una sotto-cartella oppure usa "Riautorizza Accesso" se il percorso base è già configurato.'
        );
      } else {
        toast.error(`Errore nella selezione della cartella: ${err?.message || 'sconosciuto'}`);
      }
    }
  };

  const handleFolderDialogReauthAndSubmit = async () => {
    if (inElectron) {
      setShowFolderDialog(false);
      setPendingSubmit(false);
      await doSubmit(null);
      return;
    }
    const granted = await requestPermissionAndReload();
    if (granted) {
      try {
        const db = await openDB('studio-gori-filesystem', 2);
        const stored = await db.get('handles', 'practice-base-path');
        if (stored?.handle) {
          setShowFolderDialog(false);
          await doSubmit(stored.handle);
          setPendingSubmit(false);
          return;
        }
      } catch { /* fallback */ }
      toast.error('Handle non disponibile dopo autorizzazione.');
    } else {
      toast.error('Permesso negato. Prova con "Seleziona Cartella".');
    }
  };

  const handleFolderDialogSkip = async () => {
    setShowFolderDialog(false);
    await doSubmit(null);
    setPendingSubmit(false);
  };

  const selectedType = practiceTypes.find(t => t.id === formData.typeId);
  const selectedClient = clients.find(c => c.id === formData.clientId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => parentId ? navigate(`/practices/${parentId}`) : navigate('/practices')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {parentId ? 'Torna alla pratica padre' : 'Torna alle pratiche'}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {parentId ? 'Nuova sotto-pratica' : 'Nuova Pratica'}
            </h1>
            {parentPractice ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Sotto-pratica di: {parentPractice.practiceNumber ? `#${parentPractice.practiceNumber} — ` : ''}{parentPractice.title}
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Crea una nuova pratica seguendo i passaggi
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = currentStepIndex > index;
              const stepNumber = index + 1;

              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center font-semibold
                        ${isActive ? 'bg-blue-600 text-white' : ''}
                        ${isCompleted ? 'bg-green-600 text-white' : ''}
                        ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-600' : ''}
                      `}
                    >
                      {isCompleted ? '✓' : stepNumber}
                    </div>
                    <div className="mt-2 text-center">
                      <div className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{step.description}</div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-1 flex-1 mx-4 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStepIndex].title}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Tipo Pratica */}
          {currentStep === 'type' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="typeId">Tipo Pratica *</Label>
                {typesLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    Caricamento...
                  </div>
                ) : (
                  <Combobox
                    options={practiceTypes
                      .filter(type => type.isActive)
                      .sort((a, b) => a.order - b.order)
                      .map((type) => ({
                        value: type.id,
                        label: `${type.icon || ''} ${type.name}`.trim(),
                        description: type.description || undefined,
                      }))}
                    value={formData.typeId}
                    onValueChange={(value) => setFormData({ ...formData, typeId: value })}
                    placeholder="Seleziona tipo pratica"
                    searchPlaceholder="Cerca tipo pratica..."
                    emptyMessage="Nessun tipo pratica trovato"
                  />
                )}
                {selectedType && selectedType.description && (
                  <p className="mt-2 text-sm text-gray-600">{selectedType.description}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Informazioni */}
          {currentStep === 'info' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="practiceNumber">Numero Pratica *</Label>
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <Input
                      id="practiceNumber"
                      value={formData.practiceNumber}
                      onChange={(e) => setFormData({ ...formData, practiceNumber: e.target.value.toUpperCase() })}
                      placeholder="Es: 5494 (max 6 caratteri)"
                      maxLength={6}
                    />
                  </div>
                  {loadingNextNumber && (
                    <span className="shrink-0 mt-1.5 text-xs text-muted-foreground animate-pulse">Calcolo...</span>
                  )}
                  {!loadingNextNumber && suggestedNextNumber !== null && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 mt-0.5 text-xs"
                      onClick={() => setFormData({ ...formData, practiceNumber: String(suggestedNextNumber) })}
                      title="Prossimo numero disponibile sul NAS"
                    >
                      Suggerito: {suggestedNextNumber}
                    </Button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {loadingNextNumber
                    ? 'Scansione cartelle NAS in corso...'
                    : inElectron && suggestedNextNumber !== null
                      ? `Prossimo numero libero sul NAS: ${suggestedNextNumber}`
                      : 'Numero identificativo della pratica (per cartelle NAS e faldoni fisici)'}
                </p>
              </div>

              <div>
                <Label htmlFor="clientId">Cliente *</Label>
                {clientsLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center">
                    Caricamento...
                  </div>
                ) : (
                  <Combobox
                    options={clients.map(client => ({
                      value: client.id,
                      label: client.companyName
                        || `${client.lastName || ''} ${client.firstName || ''}`.trim()
                        || `${client.firstName} ${client.lastName}`.trim(),
                      description: client.email || undefined,
                    }))}
                    value={formData.clientId}
                    onValueChange={(value) => setFormData({ ...formData, clientId: value })}
                    placeholder="Seleziona cliente"
                    searchPlaceholder="Cerca cliente..."
                    emptyMessage="Nessun cliente trovato"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="title">Titolo Pratica *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Es: SCIA per ristrutturazione Via Roma 123"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Si compila in automatico quando selezioni il cliente
                </p>
              </div>

              <div>
                <Label htmlFor="interventionLocation">Luogo intervento</Label>
                <Input
                  id="interventionLocation"
                  value={formData.interventionLocation}
                  onChange={(e) => setFormData({ ...formData, interventionLocation: e.target.value })}
                  placeholder="Es: Via Roma 123, Milano"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Indirizzo/luogo dove verrà effettuato l'intervento (per nome cartella documenti)
                </p>
              </div>

              <div>
                <Label htmlFor="assignedToId">Operatore Assegnato</Label>
                <Combobox
                  options={[
                    { value: '', label: 'Nessuno (da assegnare)' },
                    ...users.map(u => ({
                      value: u.id,
                      label: `${u.firstName} ${u.lastName}`,
                      description: u.email || undefined,
                    })),
                  ]}
                  value={formData.assignedToId}
                  onValueChange={(value) => setFormData({ ...formData, assignedToId: value })}
                  placeholder="Seleziona operatore"
                  searchPlaceholder="Cerca operatore..."
                  emptyMessage="Nessun operatore trovato"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Le attività predefinite saranno assegnate automaticamente a questo operatore
                </p>
              </div>

              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dettagliata della pratica..."
                  rows={5}
                />
              </div>

            </div>
          )}

          {/* Step 3: Scadenze */}
          {currentStep === 'dates' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="startDate">Data Inizio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Per pratiche vecchie, inserire la data di inizio originale
                </p>
              </div>

              <div>
                <Label htmlFor="priority">Priorità</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Bassa</SelectItem>
                    <SelectItem value="MEDIUM">Media</SelectItem>
                    <SelectItem value="HIGH">Alta</SelectItem>
                    <SelectItem value="URGENT">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dueDate">Data Scadenza (GG MM AAAA)</Label>
                <DatePickerItalian
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={(value) => setFormData({ ...formData, dueDate: value })}
                  placeholder="GG MM AAAA (es: 15 12 2025)"
                />
                {formData.dueDate && formData.dueDate.length === 10 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {(() => {
                      const parsed = parseItalianDateInput(formData.dueDate);
                      return parsed ? formatItalianDate(parsed) : 'Data non valida';
                    })()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Riepilogo */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3">
                  {parentPractice ? 'Riepilogo sotto-pratica' : 'Riepilogo Pratica'}
                </h3>
                <div className="space-y-2 text-sm">
                  {parentPractice && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pratica padre:</span>
                      <span className="font-medium">
                        {parentPractice.practiceNumber ? `#${parentPractice.practiceNumber} — ` : ''}{parentPractice.title}
                      </span>
                    </div>
                  )}
                  {formData.practiceNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° Pratica:</span>
                      <span className="font-medium font-mono">{formData.practiceNumber}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tipo:</span>
                    <span className="font-medium">{selectedType?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Titolo:</span>
                    <span className="font-medium">{formData.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cliente:</span>
                    <span className="font-medium">
                      {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Operatore:</span>
                    <span className="font-medium">
                      {formData.assignedToId
                        ? (() => { const u = users.find(u => u.id === formData.assignedToId); return u ? `${u.firstName} ${u.lastName}` : 'N/A'; })()
                        : 'Non assegnato'}
                    </span>
                  </div>
                  {formData.interventionLocation && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Luogo intervento:</span>
                      <span className="font-medium">{formData.interventionLocation}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 shrink-0">Percorso NAS (server):</span>
                    <span className="font-medium text-right break-all text-xs">{documentsNasRoot}</span>
                  </div>
                  {formData.description && (
                    <div>
                      <span className="text-gray-600">Descrizione:</span>
                      <p className="mt-1 text-gray-800">{formData.description}</p>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data Inizio:</span>
                    <span className="font-medium">{formData.startDate ? new Date(formData.startDate).toLocaleDateString('it-IT') : 'Oggi'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Priorità:</span>
                    <span className="font-medium">{formData.priority}</span>
                  </div>
                  {formData.dueDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scadenza:</span>
                      <span className="font-medium">
                        {(() => {
                          const parsed = parseItalianDateInput(formData.dueDate);
                          return parsed ? formatItalianDate(parsed) : formData.dueDate;
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStepIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Indietro
            </Button>
            <div className="flex gap-2">
              {currentStep === 'review' ? (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Creazione...' : 'Crea Pratica'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canGoNext()}
                >
                  Avanti
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog per selezione/autorizzazione cartella al submit */}
      <Dialog open={showFolderDialog} onOpenChange={(open) => { if (!open) { setShowFolderDialog(false); setPendingSubmit(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seleziona cartella documenti</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {inElectron
              ? 'La cartella verrà creata automaticamente usando il percorso NAS in Impostazioni. Premi "Continua" per procedere.'
              : hasSavedPath
                ? 'Il permesso di accesso alla cartella è scaduto. Puoi riautorizzare l\'accesso o selezionare una nuova cartella.'
                : `Seleziona la cartella NAS (di solito l'ultimo segmento del percorso: ${uncPathLastSegment(documentsNasRoot) || 'Volume_1'}) prima di procedere.`}
          </p>
          <DialogFooter className="flex gap-2 sm:gap-2">
            {inElectron ? (
              <>
                <Button onClick={handleFolderDialogReauthAndSubmit}>
                  Continua
                </Button>
                <Button variant="ghost" onClick={handleFolderDialogSkip}>
                  Salta
                </Button>
              </>
            ) : (
              <>
                {hasSavedPath && (
                  <Button onClick={handleFolderDialogReauthAndSubmit}>
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Riautorizza Accesso
                  </Button>
                )}
                <Button variant={hasSavedPath ? 'outline' : 'default'} onClick={handleFolderDialogSelect}>
                  <FolderOpen className="h-4 w-4 mr-2" />
                  {hasSavedPath ? 'Cambia Cartella' : 'Seleziona Cartella'}
                </Button>
                <Button variant="ghost" onClick={handleFolderDialogSkip}>
                  Salta
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showNasMatchDialog}
        onOpenChange={(open) => {
          if (!open && nasChoiceResolverRef.current) {
            resolveNasChoice({ action: 'create' });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cartelle già presenti sul NAS</DialogTitle>
            <DialogDescription>
              Esistono una o più cartelle il cui nome inizia con il numero pratica <span className="font-mono font-semibold">{formData.practiceNumber}</span>.
              Puoi associare una cartella esistente oppure crearne una nuova secondo il template del tipo pratica.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Cartella da associare</Label>
              <Select value={selectedNasFolder} onValueChange={setSelectedNasFolder}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona cartella" />
                </SelectTrigger>
                <SelectContent>
                  {nasMatchCandidates.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="nas-subfolders"
                checked={nasAssociateCreateSubfolders}
                onCheckedChange={(c) => setNasAssociateCreateSubfolders(c === true)}
              />
              <Label htmlFor="nas-subfolders" className="text-sm font-normal cursor-pointer">
                Crea le sottocartelle standard del tipo pratica se mancanti
              </Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => resolveNasChoice({ action: 'create' })}
            >
              Crea nuova cartella (template)
            </Button>
            <Button
              onClick={() =>
                resolveNasChoice({
                  action: 'associate',
                  folderName: selectedNasFolder,
                  createSubfolders: nasAssociateCreateSubfolders,
                })
              }
              disabled={!selectedNasFolder}
            >
              Associa cartella selezionata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duplicateDialog.open} onOpenChange={(open) => { if (!open) setDuplicateDialog({ open: false, existingPractice: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Numero pratica già esistente</DialogTitle>
            <DialogDescription>
              Esiste già una pratica con il numero <span className="font-mono font-semibold">{duplicateDialog.existingPractice?.practiceNumber}</span>:
            </DialogDescription>
          </DialogHeader>
          {duplicateDialog.existingPractice && (
            <div className="rounded-lg border p-3 bg-muted/50 text-sm space-y-1">
              <div className="font-medium">{duplicateDialog.existingPractice.title}</div>
              {duplicateDialog.existingPractice.client && (
                <div className="text-muted-foreground">
                  Cliente: {duplicateDialog.existingPractice.client.firstName} {duplicateDialog.existingPractice.client.lastName}
                </div>
              )}
              {duplicateDialog.existingPractice.type && (
                <div className="text-muted-foreground">
                  Tipo: {duplicateDialog.existingPractice.type.name}
                </div>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            Puoi creare questa come <strong>sotto-pratica</strong> della pratica esistente, oppure tornare indietro e cambiare il numero.
          </p>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateDialog({ open: false, existingPractice: null });
                setCurrentStep('info');
              }}
            >
              Cambia numero
            </Button>
            <Button
              onClick={async () => {
                const existing = duplicateDialog.existingPractice;
                setDuplicateDialog({ open: false, existingPractice: null });
                if (existing) {
                  setParentId(existing.id);
                  setParentPractice({
                    id: existing.id,
                    title: existing.title,
                    practiceNumber: existing.practiceNumber,
                    clientId: formData.clientId,
                    client: existing.client,
                  });
                  await doSubmitAsSubPractice(existing.id);
                }
              }}
            >
              Crea come sotto-pratica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

